import React from "react";
import { Link } from "react-router-dom";
import { Printer, Clock, Coffee, BarChart3, FileSpreadsheet, Smartphone, ChevronRight, Check, Sparkles, Ticket, Scissors } from "lucide-react";

const Y_HERO = "/yasamaru/hero.png";
const Y_TENKI = "/yasamaru/tenki.png";
const Y_CHEER = "/yasamaru/cheer.png";
const Y_BREAK = "/yasamaru/break.png";

export default function Flyer() {
  const print = () => window.print();
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&color=20-83-45&bgcolor=FFFFFF&margin=0&data=${encodeURIComponent("https://yasamarutime.online")}`;

  return (
    <div className="bg-stone-100 min-h-screen">
      {/* Print controls — hidden when printing */}
      <div className="print:hidden sticky top-0 z-20 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/" className="text-sm text-stone-500 hover:underline">← ホームへ</Link>
          <span className="text-xs text-stone-400 ml-2">A4両面チラシ プレビュー</span>
        </div>
        <button onClick={print} data-testid="print-flyer-btn"
          className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-2 text-sm inline-flex items-center gap-2">
          <Printer size={14} /> 印刷 / PDF保存
        </button>
      </div>

      <div className="flex flex-col items-center gap-6 py-6 print:p-0 print:m-0 print:gap-0">

        {/* ========== PAGE 1 (表面) ========== */}
        <div className="flyer-page bg-white shadow-2xl print:shadow-none relative overflow-hidden">

          {/* Hero band */}
          <div className="relative px-10 pt-7 pb-5 bg-gradient-to-br from-emerald-50 via-stone-50 to-amber-50 border-b border-stone-100">
            <div className="absolute -top-6 -right-6 w-44 h-44 rounded-full bg-emerald-100/70" />
            <div className="absolute top-8 right-24 w-20 h-20 rounded-full bg-amber-100/70" />

            <div className="relative flex items-start justify-between gap-6">
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-xs font-bold px-3 py-1">
                  <Sparkles size={11} /> 中小企業向け勤怠SaaS
                </div>
                <h1 className="font-display text-[50px] leading-[1.05] font-black text-stone-900 mt-2 tracking-tight">
                  やさまるタイム
                </h1>
                <p className="text-stone-700 mt-1.5 text-[15px] leading-snug font-semibold max-w-[28em]">
                  かんたん打刻、しっかり集計。<br/>
                  小さな会社の<span className="text-emerald-600">勤怠を「やさしく、まるごと」</span>。
                </p>
                <div className="mt-2.5 inline-flex items-center gap-2 rounded-2xl bg-white/80 border border-stone-200 px-3.5 py-1.5">
                  <Clock size={14} className="text-emerald-600" />
                  <span className="text-xs text-stone-700 font-semibold">2週間 無料でお試し ・ クレジットカードのみ</span>
                </div>
              </div>
              <img src={Y_HERO} alt="やさまる" className="w-[135px] shrink-0 -mr-2" />
            </div>
          </div>

          {/* Pain → Solution */}
          <div className="px-10 py-3 grid grid-cols-3 gap-2.5">
            <PainCard emoji="📝" title="紙のタイムカードがめんどう" />
            <PainCard emoji="🧮" title="月末の集計でいつも残業" />
            <PainCard emoji="💸" title="勤怠ソフトは大企業向けで高い" />
          </div>

          <div className="px-10 pb-3">
            <div className="rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-3.5 flex items-center gap-3">
              <img src={Y_CHEER} alt="やさまる" className="w-16 shrink-0" />
              <div>
                <div className="text-[10px] font-bold text-emerald-700">やさまるタイムなら</div>
                <p className="font-display font-black text-stone-900 text-[18px] leading-tight mt-0.5">
                  スマホでタップ打刻、<span className="text-emerald-600">集計はワンクリック</span>。
                </p>
                <p className="text-stone-700 text-xs mt-0.5">紙もExcelも不要。給与計算もCSV1枚で完結します。</p>
              </div>
            </div>
          </div>

          {/* Features 4 cards */}
          <div className="px-10 pb-3">
            <h2 className="font-display text-base font-black text-stone-900 mb-2 inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-4 bg-emerald-500 rounded-sm" />
              主な機能
            </h2>
            <div className="grid grid-cols-2 gap-2.5">
              <FeatureCard icon={<Smartphone size={16} />} title="スマホで打刻" desc="出勤・退勤・休憩開始/終了をワンタップ。" />
              <FeatureCard icon={<BarChart3 size={16} />} title="月次レポート&人件費" desc="時給×実労働で人件費を自動集計。" />
              <FeatureCard icon={<Coffee size={16} />} title="休憩自動補完" desc="打刻し忘れた日も設定分数で一括処理。" />
              <FeatureCard icon={<FileSpreadsheet size={16} />} title="CSV出力" desc="日本語ヘッダで給与ソフトに直貼り。" />
            </div>
          </div>

          {/* Pricing */}
          <div className="px-10 pb-3">
            <h2 className="font-display text-base font-black text-stone-900 mb-2 inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-4 bg-amber-500 rounded-sm" />
              シンプル料金プラン
            </h2>
            <div className="rounded-3xl border-2 border-amber-200 bg-amber-50/60 p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="text-[10px] font-bold text-amber-700">通常プラン（30名まで一律）</div>
                <div className="flex items-end gap-1 mt-1">
                  <span className="font-display text-[36px] font-black text-stone-900 leading-none tabular-nums">¥1,500</span>
                  <span className="text-stone-600 pb-1 text-sm">/ 月（税抜）</span>
                </div>
                <div className="text-[11px] text-stone-700 mt-0.5">＝ 1名あたり <span className="font-bold">月50円〜</span></div>
                <ul className="mt-2 space-y-0.5 text-xs text-stone-700">
                  <CheckLine>14日間の無料お試し</CheckLine>
                  <CheckLine>カスタムCSV出力 +¥15,000(一回お支払い)</CheckLine>
                  <CheckLine>複数拠点・部署分け +¥800/月(任意)</CheckLine>
                </ul>
              </div>
              <img src={Y_TENKI} alt="やさまる" className="w-20 shrink-0" />
            </div>
          </div>

          {/* CTA */}
          <div className="mt-auto bg-stone-900 text-white px-10 py-4">
            <div className="flex items-center gap-5">
              <div className="flex-1">
                <div className="text-[10px] text-amber-300 font-bold">いますぐ無料ではじめる</div>
                <div className="font-display text-xl font-black mt-0.5 leading-tight">
                  yasamarutime.online
                </div>
                <div className="text-[10px] text-stone-300 mt-0.5">→裏面の特別クーポンで合計2か月＋2週間 無料</div>
              </div>
              <div className="bg-white p-1.5 rounded-xl">
                <img src={qr} alt="QR" className="w-[78px] h-[78px]" />
                <div className="text-[8px] text-stone-700 text-center mt-0.5 font-semibold">スマホでスキャン</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-10 py-1.5 text-[9px] text-stone-500 flex items-center justify-between border-t border-stone-100">
            <span>やさまるタイム / yasamarutime.online</span>
            <span>1 / 2</span>
          </div>
        </div>

        {/* ========== PAGE 2 (裏面) ========== */}
        <div className="flyer-page bg-white shadow-2xl print:shadow-none relative overflow-hidden">

          {/* Top header strip */}
          <div className="px-10 pt-6 pb-3 bg-gradient-to-r from-stone-50 to-emerald-50 border-b border-stone-100 flex items-center justify-between">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700">
                <Sparkles size={11} /> 実際の画面で見る、やさまるタイム
              </div>
              <h2 className="font-display text-[30px] font-black text-stone-900 leading-tight mt-1">
                <span className="text-emerald-600">タップだけ</span>で勤怠管理が完了
              </h2>
            </div>
            <img src={Y_BREAK} alt="やさまる" className="w-[78px]" />
          </div>

          {/* UI Showcase: 3 screens */}
          <div className="px-10 pt-3.5">
            <div className="grid grid-cols-3 gap-2.5">
              <UICard
                src="/screenshots/employee_dashboard.png"
                title="従業員ダッシュボード"
                desc="出勤・退勤・休憩4ボタン。やさまるが応援。"
                tag="従業員"
                tagColor="bg-emerald-100 text-emerald-800"
              />
              <UICard
                src="/screenshots/admin_attendance.png"
                title="勤怠一覧"
                desc="全員の出退勤を一画面で把握、編集も可能。"
                tag="管理者"
                tagColor="bg-indigo-100 text-indigo-800"
              />
              <UICard
                src="/screenshots/admin_report.png"
                title="月次レポート&人件費"
                desc="時給×実労働を自動集計、CSV出力可能。"
                tag="管理者"
                tagColor="bg-indigo-100 text-indigo-800"
              />
            </div>
          </div>

          {/* Mobile-friendly callout */}
          <div className="px-10 pt-3 pb-3">
            <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-3.5 flex items-center gap-4">
              <img src="/screenshots/mobile_admin_attendance.png" alt="モバイル" className="w-[88px] rounded-xl shadow-md border border-stone-200" />
              <div className="flex-1">
                <div className="text-[10px] font-bold text-amber-700">どこでも管理</div>
                <p className="font-display font-black text-stone-900 text-[18px] leading-tight mt-0.5">
                  スマホ・タブレット完全対応
                </p>
                <p className="text-stone-700 text-xs mt-0.5">
                  打刻はもちろん、月次レポートも給与CSV出力もスマホからOK。<br/>
                  店長さんの「移動時間」をやさまるが減らします。
                </p>
              </div>
            </div>
          </div>

          {/* Coupon — the centerpiece of back side */}
          <div className="px-10 pb-3">
            <div className="rounded-3xl bg-stone-900 text-white p-0 overflow-hidden relative shadow-2xl">
              <div className="absolute top-2 right-3 text-[9px] text-stone-400 font-mono">CAMPAIGN-2026</div>

              <div className="grid grid-cols-5">
                {/* Left: design */}
                <div className="col-span-3 px-6 py-4">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-stone-900 text-[10px] font-black px-2.5 py-0.5">
                    <Ticket size={11} /> このチラシ限定 特別クーポン
                  </div>
                  <div className="font-display text-[36px] font-black leading-tight mt-1.5">
                    <span className="text-amber-300">2か月</span>＋14日<br/>
                    <span className="text-emerald-300">無料</span>!
                  </div>
                  <p className="text-stone-300 text-[11px] mt-1 leading-snug">合計 約74日間、¥0でじっくりお試しいただけます。途中で解約された場合、ご請求は発生しません。</p>
                </div>

                {/* Right: code stub */}
                <div className="col-span-2 bg-white text-stone-900 px-4 py-4 relative">
                  <div className="absolute -left-2.5 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
                    {[...Array(8)].map((_, i) => <span key={i} className="block w-2.5 h-2.5 rounded-full bg-stone-100 border border-stone-200" />)}
                  </div>
                  <Scissors size={11} className="absolute -top-1 left-2 text-stone-300 rotate-90" />
                  <div className="text-[9px] text-stone-500 font-bold">クーポンコード</div>
                  <div className="font-display font-black text-[26px] tracking-[0.1em] text-emerald-700 mt-0.5 tabular-nums">
                    YASAI808
                  </div>
                  <div className="text-[9px] text-stone-500 mt-1.5 leading-snug">
                    <p>① yasamarutime.online でサインアップ</p>
                    <p>② カード登録画面で上記コードを入力</p>
                    <p>③ 「適用」ボタンを押す → 完了</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why us? */}
          <div className="px-10 pb-3">
            <h2 className="font-display text-sm font-black text-stone-900 mb-1.5 inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-4 bg-emerald-500 rounded-sm" />
              選ばれる3つの理由
            </h2>
            <div className="grid grid-cols-3 gap-2.5">
              <ReasonCard num="01" title="導入5分" desc="サインアップしたらすぐ使えます。難しい設定なし。" />
              <ReasonCard num="02" title="シンプル料金" desc="¥1,500 / 月 一律。30名まで同じ価格。" />
              <ReasonCard num="03" title="日本語サポート" desc="やさまるが画面で案内。困ったらメールでOK。" />
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-auto px-10 py-3 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <div className="flex items-center gap-5">
              <div className="flex-1">
                <div className="text-[10px] text-emerald-100 font-bold">お申し込みはこちら</div>
                <div className="font-display text-lg font-black mt-0.5 leading-tight">
                  yasamarutime.online
                </div>
                <div className="text-[10px] mt-0.5 inline-flex items-center gap-1">
                  クーポンコード <span className="font-mono font-bold bg-white text-emerald-700 rounded px-1.5 py-0.5">YASAI808</span>
                </div>
              </div>
              <div className="bg-white p-1.5 rounded-xl">
                <img src={qr} alt="QR" className="w-[72px] h-[72px]" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-10 py-1.5 text-[9px] text-stone-500 flex items-center justify-between border-t border-stone-100">
            <span>やさまるタイム / yasamarutime.online</span>
            <span>2 / 2</span>
          </div>
        </div>
      </div>

      <style>{`
        .flyer-page {
          width: 210mm;
          min-height: 297mm;
          display: flex;
          flex-direction: column;
          font-family: 'Hiragino Sans', 'Yu Gothic', 'Noto Sans JP', sans-serif;
        }
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .flyer-page {
            box-shadow: none !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden;
            page-break-after: always;
          }
          .flyer-page:last-child { page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}

function PainCard({ emoji, title }) {
  return (
    <div className="rounded-2xl bg-white border border-stone-200 px-3 py-3 text-center">
      <div className="text-2xl">{emoji}</div>
      <div className="text-xs font-semibold text-stone-700 mt-1 leading-snug">{title}</div>
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-stone-200 p-3.5 bg-white">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">{icon}</span>
        <div className="font-display font-bold text-stone-900 text-[15px]">{title}</div>
      </div>
      <div className="text-xs text-stone-600 mt-1.5 leading-relaxed">{desc}</div>
    </div>
  );
}

function CheckLine({ children }) {
  return (
    <li className="flex items-start gap-1.5">
      <Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function UICard({ src, title, desc, tag, tagColor }) {
  return (
    <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
      <div className="bg-gradient-to-br from-stone-50 to-emerald-50 h-[150px] overflow-hidden flex items-start justify-center border-b border-stone-100">
        <img src={src} alt={title} className="w-full object-cover object-top" />
      </div>
      <div className="px-3 py-2.5">
        <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${tagColor}`}>{tag}</span>
        <div className="font-display font-bold text-stone-900 text-[13px] mt-1">{title}</div>
        <div className="text-[11px] text-stone-600 leading-snug mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function ReasonCard({ num, title, desc }) {
  return (
    <div className="rounded-2xl border border-stone-200 p-3.5 bg-white">
      <div className="font-display text-[26px] font-black text-emerald-500 leading-none">{num}</div>
      <div className="font-display font-bold text-stone-900 text-sm mt-1">{title}</div>
      <div className="text-[11px] text-stone-600 mt-1 leading-relaxed">{desc}</div>
    </div>
  );
}
